import { Injectable, Inject, inject } from '@angular/core';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { OlLayerService } from './ol/ol-layer.service';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Circle as CircleStyle, Icon, Circle, Fill, Stroke, Style, Text } from 'ol/style';
import Map from 'ol/Map';

import { TT_LIB_CONFIG } from '../tt-lib.config.token';
import { AppConfig, mouseProjection } from '../interfaces/app-config';

import { MapService } from './map.service';
import { PopupService } from './popup.service';

@Injectable({
  providedIn: 'root'
})
export class MarkerService {

  private static readonly POINT_TYPES = ['Point', 'MultiPoint'];
  private static readonly BLEND_TYPES = ['LineString', 'MultiLineString', 'Polygon', 'MultiPolygon'];

  static markerCodes = {
    default: 0xe062,
    defaultFilled: 0x2608,
    toponym: 0x2616,
    street: 0x2628,
    address: 0x2617,
    featureInfo: 0x260B,
    coords: 0x260C,
    remark: 0xe062,
    ferrarisWig: 0x2b24,
    walkferraris: 0xe145,
    municipality: 0x2610,
    mapindex: 0x260C,
  };
  
  private ngiOrangeColor = 'rgba(234, 89, 1, 1.0)';
  private ngiOrangeColorDarker = 'rgba(225, 62, 1, 1.0)';
  private ngiOrangeColorVeryDark = 'rgba(141, 38, 0, 1.0)';
  private ngiDarkBlueColor = 'rgba(44, 60, 86, 1.0)';
  private ngiBlueColor = 'rgba(0, 108, 157, 1.0)';
  private ngiBlueGreenColor = 'rgba(6, 138, 155, 1.0)';
  private ngiGreenBlueColor = 'rgba(47, 162, 152, 1.0)';
  private ngiDarkGreenBlueColor = 'rgba(29, 134, 123, 1.0)';
  private ngiGrayColor = 'rgba(194, 194, 194, 1.0)';
  private ngiDarkGrayColor = 'rgba(97, 97, 97, 1.0)';
  // private highlightColor = 'rgba(224, 190, 12, 0.1)';
  private ngiBlueHighlightColor = 'rgba(0, 108, 157, 0.1)';
  private ngiBlueGreenHighlightColor = 'rgba(6, 138, 155, 0.1)';
  private ngiOrangeColorHighlightColor = 'rgba(234, 89, 1, 0.1)';
  private ngiOrangeColorVeryDarkHighlightColor = 'rgba(141, 38, 0, 0.1)';
  private ngiYellowColorHighlightColor = 'rgba(141, 122, 0, 0.1)';
  private highlightFullColor = 'rgba(224, 190, 12, 1.0)';

  private map: Map;
  public markerLayerSource: VectorSource;
  public markerLayer: VectorLayer<any>;        // points
  public markerLayerBlend: VectorLayer<any>;   // multiply in css

  private highlightColor: any;
  private defaultColor: any;

  private ttLibConfig = inject<AppConfig>(TT_LIB_CONFIG);

  constructor(
    private mapService: MapService,
    private olLayerService: OlLayerService,
    private popup: PopupService,
  ) {
    this.markerLayerSource = new VectorSource();
    this.markerLayer = new VectorLayer({
      source: this.markerLayerSource,
      visible: false,
      style: (feature) => this._styleForTypes(feature as Feature, MarkerService.POINT_TYPES),
    });

    this.markerLayer.set('customId', 'markerLayer');
    this.markerLayer.setZIndex(10001);

    this.markerLayerBlend = new VectorLayer({
      source: this.markerLayerSource,
      visible: true,
      className: 'markerLayerBlend multiply',
      style: (feature) => this._styleForTypes(feature as Feature, MarkerService.BLEND_TYPES),
    });
    this.markerLayerBlend.set('customId', 'markerLayerBlend');
    this.markerLayerBlend.setZIndex(10000);

    this.mapService.setPointerCursorOnFeatureHoverForLayerId('markerLayer');
    this.mapService.setPointerCursorOnFeatureHoverForLayerId('markerLayerBlend');

    this.popup.onPopupClose$.subscribe(() => { this.removeHighlightedMarker(); });
    this.defaultColor = (this.ttLibConfig.map.overlays) ? this.ttLibConfig.map.overlays.defaultColor : [53, 53, 53, 1.0];
    this.highlightColor = (this.ttLibConfig.map.overlays) ? this.ttLibConfig.map.overlays.highlightColor : [253, 15, 91, 1.0];
  }

  add(marker: Feature) {
    this.olLayerService.getOlEnabledLayers().forEach(layer => {});
    ['markerLayer', 'markerLayerBlend'].forEach(id => {
      const layer = this.olLayerService.getLayerById(id);
      if (layer && !layer.getVisible()) {
        layer.setVisible(true);
      }
    });
    if (!this.olLayerService.getLayerById("markerLayer")) {
      this.olLayerService.addOlLayerToMap(this.markerLayer);
      this.olLayerService.addOlLayerToMap(this.markerLayerBlend);
      this.mapService.mapClickOnMarker$.subscribe((marker) => (marker) ? this.handleMarkerClick(marker) : this.outsideMarkerClick());
    }
    this.setMarkerStyle(marker);
    this.markerLayerSource.addFeature(marker);
  }

  setMarkerStyle(marker: Feature) {
    marker.setStyle(null);
    if (marker.get('highlight')) {
      this.highlight(marker);
    }
  }

  private highlight(marker: Feature) {
    this.unHighlightMarkers();
    marker.set('highlight', true);
    this.markerLayerSource.changed();
  }

  private unHighlight(marker: Feature) {
    marker.set('highlight', false);
    this.markerLayerSource.changed();
  }

  private unHighlightMarkers() {
    this.markerLayerSource.getFeatures().forEach(marker => {
      if (marker.get('highlight')) {
        this.unHighlight(marker);
      }
    });
  }

  private removeHighlightedMarker() {
    this.markerLayerSource.getFeatures().forEach(marker => {
      if (marker.get('highlight')) {
        this.remove(marker);
      }
    });
  }

  hasThisMarker(feature: Feature): boolean {
    return this.markerLayerSource.hasFeature(feature);
  }

  remove(feature: Feature) {
    this.markerLayerSource.removeFeature(feature);
  }

  clearAll() {
    this.markerLayerSource.clear();
  }

  private handleMarkerClick(marker: Feature) {
    if (marker.get('type') === 'ContextMenuFeature'
      && this.mapService.wasContextMenuEvent()
      && (this.mapService.contextMenuTapHoldEnd > -1 && (Date.now() - this.mapService.contextMenuTapHoldEnd) < 500)) {
      return;
    }

    if (marker.get('highlight')) {
      this.unHighlight(marker);
    } else {
      this.unHighlightMarkers();
      this.highlight(marker);
    }
  }

  private outsideMarkerClick() {
    if (this.mapService.wasContextMenuEvent()
      && (this.mapService.contextMenuTapHoldEnd > -1 && (Date.now() - this.mapService.contextMenuTapHoldEnd) < 500)) {
      return;
    }

    this.unHighlightMarkers();
  }

  private createMarkerStyle(marker: Feature): Style[] {
    const styleClass: string = marker.get('styleClass') || 'default';
    let charCode = ((styleClass in MarkerService.markerCodes) ? (MarkerService.markerCodes as any)[styleClass] : MarkerService.markerCodes.default);
    let style: Style[] = [];

    switch (marker.getGeometry()?.getType()) {
      case 'Point':
      case 'MultiPoint': {
        style = [
          new Style({
            text: new Text({
              font: '30px "NGI-IGN_ICONS"',
              text: String.fromCharCode(charCode),
              textAlign: 'center',
              offsetY: -13,
              fill: new Fill({
                color: this.defaultColor
              }),
              stroke: new Stroke({
                color: [255, 255, 255, 1.0],
                width: 3
              })
            }),
          }),
          //add a little white circle behind the marker to cover the hole, the font software support only monochrome (in free mode) output
          new Style({
            image: new CircleStyle({
              displacement: [0, 19],
              radius: 9,
              fill: new Fill({
                color: [255, 255, 255, 1.0]
              }),
            })
          })
        ];
        break;
      }
      case 'LineString':
      case 'MultiLineString': {
        const lineColor = this.defaultColor.slice(0);
        lineColor[3] = 0.8;
        style = [
          new Style({
            stroke: new Stroke({
              color: lineColor,
              width: 11,
              lineCap: 'round',
              lineJoin: 'round'
            })
          }),
          // new Style({
          //   stroke: new Stroke({
          //     color: [255, 255, 255, 0.6],
          //     width: 0,
          //     lineCap: 'round',
          //     lineJoin: 'round'
          //   })
          // })
        ];
        break;
      }
      case 'Polygon':
      case 'MultiPolygon': {
        // const lineColor = 'rgba(129, 252, 98, 0.1)';
        const lineColor = this.defaultColor.slice(0);
        style = [
          new Style({
            stroke: new Stroke({
              color: lineColor,
              width: 5,
              lineCap: 'round',
              lineJoin: 'round'
            }),
            fill: new Fill({
              color: this.ngiBlueGreenHighlightColor,
            }),
          })
        ];
        break;
      }
    }

    return style;
  }

  private createHighlightStyle(marker: Feature): Style[] {
    let markerStyle = this.createMarkerStyle(marker);

    switch (marker.getGeometry()?.getType()) {
      case 'Point':
      case 'MultiPoint': {
        markerStyle[0].getText()?.setFill(new Fill({
          color: this.highlightColor
        }));
        markerStyle[0].setZIndex(10000);
        if (markerStyle[1]) markerStyle[1].setZIndex(10000);
        break;
      }
      case 'LineString':
      case 'MultiLineString': {
        const lineColor = this.highlightColor.slice(0);
        lineColor[3] = 0.8;
        markerStyle[0].setStroke(new Stroke({
          color: lineColor,
          width: 11,
          lineCap: 'round',
          lineJoin: 'round'
        }));
        markerStyle[0].setZIndex(10000);
        if (markerStyle[1]) markerStyle[1].setZIndex(10000);
        break;
      }
      case 'Polygon':
      case 'MultiPolygon': {
        const lineColor = this.highlightColor.slice(0);
        lineColor[3] = 1.0;
        markerStyle[0].setStroke(new Stroke({
          color: lineColor,
          width: 5,
          lineCap: 'round',
          lineJoin: 'round'
        }));
   
        markerStyle[0].setFill(new Fill({                
              color: this.ngiOrangeColorHighlightColor,          
          
        }));
        markerStyle[0].setZIndex(10000);
        break;
      }
    }

    return markerStyle;
  }

  private _styleForTypes(feature: Feature, ownedTypes: string[]): Style[] | undefined {
    const type = feature.getGeometry()?.getType();

    if (!type || !ownedTypes.includes(type)) {
      return undefined; // Hides feature on this layer
    }

    if (feature.get('highlight')) {
      return this.createHighlightStyle(feature);
    }
    return this.createMarkerStyle(feature);
  }
}